import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { AuthTags } from '~constants/authTags';

import { ClickableIcon, ContainerHeader, Modal, Spinner } from '~components';
import { faCog, faMoneyBill } from '~components/icon';
import TabContainer, { Tab } from '~components/TabContainer';
import { ClientSignupsContainer } from '~direct-debits/components/client-signups';
import { ConnectionManager } from '~direct-debits/components/connection';
import { MandateMatchingContainer } from '~direct-debits/components/mandates';
import { PaymentBatchesContainer } from '~direct-debits/components/payment-batches';

import { useGetConnectionsQuery } from '~direct-debits/actions/connections';

const tabs: Tab[] = [
  { id: 'direct-debits-client-signups', title: 'Client Signups', component: ClientSignupsContainer },
  { id: 'direct-debits-mandate-matching', title: 'Mandate Matching', component: MandateMatchingContainer },
  { id: 'direct-debits-payment-batches', title: 'Payment Batches', component: PaymentBatchesContainer },
];

const DirectDebitsContainer: React.FC = () => {
  // `isFetching` covers both the initial load and any in-flight refetch
  // (e.g. after a tag-driven invalidation), so the spinner reflects every
  // network round-trip — `isLoading` would only fire on the very first one.
  // RTK Query dedupes with the ConnectionManager's own subscription so this
  // doesn't double-fetch when the modal is open.
  const { data: connections, isFetching: isConnectionsFetching } = useGetConnectionsQuery();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const autoOpenFiredRef = useRef(false);

  const hasActiveConnection = useMemo(
    () => (connections ?? []).some((connection) => connection.status === 'active'),
    [connections],
  );

  useEffect(() => {
    // Only fires once per page load, after the initial data fetch has resolved.
    // isFetching covers the initial load AND any background refetch — wait until
    // it settles before deciding. The ref ensures we never fire again mid-session.
    if (autoOpenFiredRef.current) return;
    if (isConnectionsFetching) return;
    autoOpenFiredRef.current = true;
    if (!hasActiveConnection) {
      setIsSettingsOpen(true);
    }
  }, [isConnectionsFetching, hasActiveConnection]);

  return (
    <div className="p-2">
      <ContainerHeader icon={faMoneyBill} iconSize="1x" className="info" dataCy="direct-debits-header">
        <div className="d-flex align-items-center gap-1">
          <span>Direct Debits - GoCardless</span>
          <ClickableIcon
            icon={faCog}
            id="direct-debits-settings-btn"
            toolTip="GoCardless Connection Settings"
            ariaLabel="GoCardless Connection Settings"
            onClick={() => setIsSettingsOpen(true)}
            authTag={AuthTags.DIRECTDEBITS.UPDATE}
            dataCy="direct-debits-settings-cog"
          />
          {connections !== undefined && (
            <span
              className={classNames('badge', hasActiveConnection ? 'bg-success' : 'bg-danger')}
              data-cy="direct-debits-connection-status-badge"
            >
              {hasActiveConnection ? 'Connected' : 'Disconnected'}
            </span>
          )}
        </div>
      </ContainerHeader>
      {isConnectionsFetching ? (
        <div className="my-3" data-testid="direct-debits-connection-loading">
          <Spinner loading />
        </div>
      ) : (
        <TabContainer tabs={tabs} setPageTitle={false} />
      )}
      <Modal
        setIsOpen={setIsSettingsOpen}
        isOpen={isSettingsOpen}
        header="GoCardless Connection"
        size="lg"
        data-cy="direct-debits-settings-modal"
      >
        <ConnectionManager />
      </Modal>
    </div>
  );
};

export default DirectDebitsContainer;
